import React from "react";
import { Box } from "@mui/material";
import LeadListContactCell from "./LeadListContactCell";

function buildTelHref(phone) {
  const s = String(phone ?? "").trim();
  if (!s || s === "-") return null;
  const normalized = s.startsWith("+")
    ? `+${s.slice(1).replace(/\D/g, "")}`
    : s.replace(/\D/g, "");
  if (!normalized || normalized === "+") return null;
  return `tel:${normalized}`;
}

export function isLeadsListTable(tableName, configuration) {
  const names = [tableName, configuration?.[0]?.table, configuration?.[0]?.heading]
    .map((v) => String(v || "").toLowerCase())
    .filter(Boolean);
  return names.some((n) => n === "leads" || n === "ems_leads");
}

/**
 * Rich multi-line leads list cells (matches CustomisetableAlter).
 * Returns null when default rendering should apply.
 */
export function renderLeadsListTableCell({
  row,
  col,
  getNestedValue,
  navigate,
}) {
  const raw = getNestedValue(row, col.key);
  const display = raw === null || raw === undefined ? "" : String(raw);
  const empty =
    !display || display === "-" || display === "null" || display === "N/A";

  if (col.key === "contact_timing_live") {
    return (
      <LeadListContactCell
        timezone={getNestedValue(row, "__lct_timezone")}
        morningStart={getNestedValue(row, "__lct_morning_start")}
        morningEnd={getNestedValue(row, "__lct_morning_end")}
        eveningStart={getNestedValue(row, "__lct_evening_start")}
        eveningEnd={getNestedValue(row, "__lct_evening_end")}
        morningIstStart={getNestedValue(row, "__lct_morning_ist_start")}
        eveningIstStart={getNestedValue(row, "__lct_evening_ist_start")}
      />
    );
  }

  if (col.key === "contact_status_dot") {
    return (
      <LeadListContactCell
        timezone={getNestedValue(row, "__lct_timezone")}
        morningStart={getNestedValue(row, "__lct_morning_start")}
        morningEnd={getNestedValue(row, "__lct_morning_end")}
        eveningStart={getNestedValue(row, "__lct_evening_start")}
        eveningEnd={getNestedValue(row, "__lct_evening_end")}
        morningIstStart={getNestedValue(row, "__lct_morning_ist_start")}
        eveningIstStart={getNestedValue(row, "__lct_evening_ist_start")}
        showTime={false}
      />
    );
  }

  if (col.key === "lead_datetime") {
    const value = String(display || "").trim();
    let datePart = value || "—";
    let timePart = "";

    const m = value.match(/^(\d{2}-\d{2}-\d{4})\s+(\d{1,2}:\d{2}\s*(?:AM|PM))$/i);
    if (m) {
      datePart = m[1];
      timePart = m[2].toUpperCase();
    } else if (value.includes(" ")) {
      const parts = value.split(/\s+/);
      if (parts.length >= 2) {
        datePart = parts[0];
        timePart = parts.slice(1).join(" ");
      }
    }

    const sourceLine = String(
      getNestedValue(row, "__lead_source_display") ?? "—"
    ).trim();

    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", minWidth: 0, lineHeight: 1.25 }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", columnGap: 0.75 }}>
          <Box component="span" sx={{ fontSize: "0.875rem" }}>{datePart}</Box>
          {timePart ? (
            <Box component="span" sx={{ fontSize: "0.875rem", color: "text.secondary", whiteSpace: "nowrap" }}>
              {timePart}
            </Box>
          ) : null}
        </Box>
        <Box component="span" sx={{ fontSize: "0.78rem", color: "text.secondary", mt: 0.35, wordBreak: "break-word", maxWidth: 220 }}>
          {sourceLine || "—"}
        </Box>
      </Box>
    );
  }

  if (col.key === "person_full_name") {
    const leadId =
      getNestedValue(row, "organization_ems_lead_id") ??
      getNestedValue(row, "id") ??
      "";
    const leadIdText =
      leadId !== null && leadId !== undefined && String(leadId).trim() !== ""
        ? String(leadId).trim()
        : "-";
    const name = display || "—";
    const parts = name.trim().split(/\s+/).filter(Boolean);
    const initials = parts.length >= 2
      ? (parts[0][0] + parts[1][0]).toUpperCase()
      : name.slice(0, 2).toUpperCase();
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
        <Box sx={{ width: 34, height: 34, borderRadius: "50%", background: "#4C5565", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "12px", fontWeight: 700, flexShrink: 0, letterSpacing: "0.03em", userSelect: "none" }}>
          {initials}
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", minWidth: 0, lineHeight: 1.25 }}>
          <Box
            component="span"
            title={name}
            sx={{
              fontSize: "0.875rem",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "100%",
            }}
          >
            {name}
          </Box>
          <Box component="span" sx={{ fontSize: "0.78rem", color: "text.secondary", mt: 0.35, whiteSpace: "nowrap" }}>
            Lead Id : {leadIdText}
          </Box>
        </Box>
      </Box>
    );
  }

  if (col.key === "lead_stage_name") {
    const stageText = display && String(display).trim() !== "" ? String(display) : "—";
    const statusText =
      String(getNestedValue(row, "__lead_status_display") ?? "-").trim() || "-";
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", minWidth: 0, lineHeight: 1.25 }}>
        <Box component="span" sx={{ fontSize: "0.875rem", wordBreak: "break-word" }}>{stageText}</Box>
        <Box component="span" sx={{ fontSize: "0.78rem", color: "text.secondary", mt: 0.35, whiteSpace: "nowrap" }}>
          {statusText}
        </Box>
      </Box>
    );
  }

  if (col.key === "country") {
    const locRaw = getNestedValue(row, "location_region");
    const locStr = locRaw !== null && locRaw !== undefined ? String(locRaw).trim() : "";
    const locationLine = locStr && locStr !== "-" ? locStr : display || "—";
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0.75, minWidth: 0, maxWidth: 300 }}>
        <Box component="span" sx={{ fontSize: "0.875rem", wordBreak: "break-word" }}>{locationLine}</Box>
        <LeadListContactCell
          timezone={getNestedValue(row, "__lct_timezone")}
          morningStart={getNestedValue(row, "__lct_morning_start")}
          morningEnd={getNestedValue(row, "__lct_morning_end")}
          eveningStart={getNestedValue(row, "__lct_evening_start")}
          eveningEnd={getNestedValue(row, "__lct_evening_end")}
          morningIstStart={getNestedValue(row, "__lct_morning_ist_start")}
          eveningIstStart={getNestedValue(row, "__lct_evening_ist_start")}
          showDot={false}
        />
      </Box>
    );
  }

  if (col.key === "phone" && !empty) {
    const href = buildTelHref(display);
    if (href) {
      return (
        <Box
          component="a"
          href={href}
          title="Call"
          onClick={(e) => e.stopPropagation()}
          sx={{
            color: "primary.main",
            textDecoration: "underline",
            textUnderlineOffset: "2px",
            cursor: "pointer",
            fontSize: "0.875rem",
            "&:hover": { opacity: 0.88 },
          }}
        >
          {display}
        </Box>
      );
    }
  }

  if (col.key === "lead_activities_count") {
    const leadId =
      row?.organization_ems_lead_id ?? row?.id ?? row?.lead_id ?? "";
    const hasLeadId =
      leadId !== undefined &&
      leadId !== null &&
      String(leadId).trim() !== "" &&
      String(leadId) !== "-";
    const latestRaw = getNestedValue(row, "__latest_lead_activity_datetime");
    const latestLabel =
      latestRaw && String(latestRaw).trim() !== "-"
        ? (() => {
            const d = new Date(String(latestRaw));
            if (!Number.isNaN(d.getTime())) {
              const dd = String(d.getDate()).padStart(2, "0");
              const mm = String(d.getMonth() + 1).padStart(2, "0");
              const yyyy = d.getFullYear();
              const hh = d.getHours();
              const mi = String(d.getMinutes()).padStart(2, "0");
              const ampm = hh >= 12 ? "PM" : "AM";
              const h12 = hh % 12 || 12;
              return `${dd}-${mm}-${yyyy} ${h12}:${mi} ${ampm}`;
            }
            return String(latestRaw);
          })()
        : "-";
    if (!empty && hasLeadId) {
      return (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
          <Box
            component="button"
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              navigate(
                `/lead-activities?organization_ems_lead_id=${encodeURIComponent(String(leadId))}`
              );
            }}
            sx={{
              p: 0,
              border: 0,
              background: "transparent",
              color: "primary.main",
              textDecoration: "underline",
              cursor: "pointer",
              font: "inherit",
            }}
          >
            {display}
          </Box>
          <Box component="span" sx={{ mt: 0.35, fontSize: "0.78rem", color: "text.secondary" }}>
            Last Activity
          </Box>
          <Box component="span" sx={{ fontSize: "0.875rem", color: "text.secondary", whiteSpace: "nowrap" }}>
            {latestLabel}
          </Box>
        </Box>
      );
    }
  }

  return null;
}
